import { useState, useEffect } from "react";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  doc,
  getDoc,
  Timestamp,
  updateDoc,
} from "firebase/firestore";
import Select from "react-select";
import Cache from "../utils/cache";

const getTodayDateString = () => {
  const now = new Date();
  const ctDateString = now.toLocaleString("en-US", {
    timeZone: "America/Chicago",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const [month, day, year] = ctDateString.split("/");
  return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
};

function toCentralDate(date = new Date()) {
  return new Date(
    date.toLocaleString("en-US", { timeZone: "America/Chicago" })
  );
}

export default function UpdateOfferPage({ uploadedBy }) {
  // --- Vendors and categories ---
  const [vendors, setVendors] = useState([]);
  const vendorOptions = vendors.map((v) => ({ value: v, label: v }));

  const [categories, setCategories] = useState([]);

  // --- Search inputs ---
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [searchProductName, setSearchProductName] = useState("");

  // --- States ---
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [status, setStatus] = useState("");

  // The matched offer (form state)
  const [offer, setOffer] = useState({
    vendor: "",
    productName: "",
    description: "",
    category: "",
    offerImage: "",
    offerLink: "",
    originalPrice: "",
    offerPrice: "",
    rebateAmount: "",
    cashback: "",
    productSKU: "",
    productUPC: "",
    instructions: "",
    freeShippingMinimum: "",
    freeStorePickup: "No",
    tags: "",
    promoCode: "",
    startDttm: getTodayDateString(),
    endDttm: "",
    offerType: "",
  });

  // Firestore doc ID and offer index to know where to update
  const [foundDocId, setFoundDocId] = useState(null);
  const [foundOfferIndex, setFoundOfferIndex] = useState(null);

  // Load vendors and categories on mount
  useEffect(() => {
    async function loadVendorsAndCategories() {
      try {
        let vendorList = [];
        const cached = Cache.getData();
        const cachedVendors = cached?.vendors;

        if (
          cachedVendors &&
          Array.isArray(cachedVendors) &&
          cachedVendors.length > 0 &&
          typeof cachedVendors[0] === "object"
        ) {
          vendorList = cachedVendors
            .map((v) => v.vendorName || v.id)
            .filter(Boolean);
        } else {
          const snapshot = await getDocs(collection(db, "vendors"));
          const groupedDocs = snapshot.docs.filter((doc) =>
            doc.id.startsWith("vendors_")
          );

          const allVendors = [];
          groupedDocs.forEach((doc) => {
            const data = doc.data();
            if (Array.isArray(data.vendors)) {
              data.vendors.forEach((vendor) => {
                if (!vendor.vendorName && vendor.id) {
                  vendor.vendorName = vendor.id;
                }
                allVendors.push(vendor);
              });
            }
          });

          vendorList = allVendors
            .map((v) => v.vendorName || v.id)
            .filter(Boolean);

          Cache.setData({
            ...cached,
            vendors: allVendors,
          });
        }
        setVendors(vendorList.sort((a, b) => a.localeCompare(b)));

        // Load categories
        const catSnap = await getDocs(collection(db, "category"));
        const allCategories = [];
        catSnap.forEach((doc) => {
          const data = doc.data();
          if (Array.isArray(data.categories)) {
            allCategories.push(...data.categories);
          }
        });
        setCategories(Array.from(new Set(allCategories)));
      } catch (error) {
        console.error("Failed to load vendors/categories", error);
      }
    }
    loadVendorsAndCategories();
  }, []);

  // Handle form input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setOffer((prev) => ({ ...prev, [name]: value }));
  };

  // Convert Firestore Timestamp to yyyy-MM-dd string for date input
  function timestampToDateInput(ts) {
    if (!ts?.toDate) return "";
    const d = ts.toDate();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }

  // Format yyyy-MM-dd to Firestore Timestamp (endDttm is end of day)
  function dateInputToTimestamp(dateStr, isEnd = false) {
    if (!dateStr) return null;
    const [year, month, day] = dateStr.split("-");
    if (isEnd) {
      return Timestamp.fromDate(
        new Date(year, month - 1, day, 23, 59, 59, 999)
      );
    } else {
      return Timestamp.fromDate(new Date(year, month - 1, day, 0, 0, 0, 0));
    }
  }

  // Search for offer based on exact vendor and productName, active only
  async function handleSearch() {
    setStatus("");
    setLoadingSearch(true);
    setFoundDocId(null);
    setFoundOfferIndex(null);

    if (!selectedVendor || !searchProductName.trim()) {
      setStatus("Please select vendor and enter product name.");
      setLoadingSearch(false);
      return;
    }

    try {
      const snapshot = await getDocs(collection(db, "product_offers"));
      const nowCentral = toCentralDate();

      let foundOffer = null;
      let docId = null;
      let offerIndex = null;

      outerLoop:
      for (const docSnap of snapshot.docs) {
        const data = docSnap.data();
        if (!Array.isArray(data.offers)) continue;

        for (let i = 0; i < data.offers.length; i++) {
          const off = data.offers[i];

          if (
            off.vendor === selectedVendor.value &&
            off.productName === searchProductName.trim()
          ) {
            // Check if active (endDttm > nowCentral)
            let endDate = null;
            if (off.endDttm?.toDate) {
              endDate = off.endDttm.toDate();
            } else if (off.endDttm instanceof Date) {
              endDate = off.endDttm;
            }
            if (endDate && endDate > nowCentral) {
              foundOffer = off;
              docId = docSnap.id;
              offerIndex = i;
              break outerLoop;
            }
          }
        }
      }

      if (!foundOffer) {
        setStatus("No active matching product offer found.");
        setOffer({
          vendor: selectedVendor.value,
          productName: searchProductName.trim(),
          description: "",
          category: "",
          offerImage: "",
          offerLink: "",
          originalPrice: "",
          offerPrice: "",
          rebateAmount: "",
          cashback: "",
          productSKU: "",
          productUPC: "",
          instructions: "",
          freeShippingMinimum: "",
          freeStorePickup: "No",
          tags: "",
          promoCode: "",
          startDttm: getTodayDateString(),
          endDttm: "",
          offerType: "",
        });
        setFoundDocId(null);
        setFoundOfferIndex(null);
        setSelectedVendor(null);
      } else {
        setStatus("Offer found. You can edit the fields below.");
        setFoundDocId(docId);
        setFoundOfferIndex(offerIndex);
        setSelectedVendor({ value: foundOffer.vendor, label: foundOffer.vendor });
        setOffer({
          vendor: foundOffer.vendor || "",
          productName: foundOffer.productName || "",
          description: foundOffer.description || "",
          category: foundOffer.category || "",
          offerImage: foundOffer.offerImage || "",
          offerLink: foundOffer.offerLink || "",
          originalPrice:
            foundOffer.originalPrice !== undefined
              ? String(foundOffer.originalPrice)
              : "",
          offerPrice:
            foundOffer.offerPrice !== undefined
              ? String(foundOffer.offerPrice)
              : "",
          rebateAmount:
            foundOffer.rebateAmount !== undefined
              ? String(foundOffer.rebateAmount)
              : "",
          cashback:
            foundOffer.cashback !== undefined ? String(foundOffer.cashback) : "",
          productSKU: foundOffer.productSKU || "",
          productUPC: foundOffer.productUPC || "",
          instructions: foundOffer.instructions || "",
          freeShippingMinimum:
            foundOffer.freeShippingMinimum !== undefined
              ? String(foundOffer.freeShippingMinimum)
              : "",
          freeStorePickup: foundOffer.freeStorePickup || "No",
          tags:
            Array.isArray(foundOffer.tags)
              ? foundOffer.tags.join(", ")
              : foundOffer.tags || "",
          promoCode: foundOffer.promoCode || "",
          startDttm: timestampToDateInput(foundOffer.startDttm),
          endDttm: timestampToDateInput(foundOffer.endDttm),
          offerType: foundOffer.offerType || "",
        });
      }
    } catch (err) {
      console.error(err);
      setStatus("Error searching offers.");
    }
    setLoadingSearch(false);
  }

  // Validate form inputs
  function validateOffer(o) {
    if (!o.vendor || !o.productName || !o.startDttm || !o.endDttm) {
      return "Vendor, Product Name, Start Date, and End Date are required.";
    }
    if (!o.offerPrice || isNaN(o.offerPrice)) {
      return "Offer Price is required and must be a number.";
    }

    const [year, month, day] = o.startDttm.split("-").map(Number);
    const startDate = new Date(year, month - 1, day);
    const [eyear, emonth, eday] = o.endDttm.split("-").map(Number);
    const endDate = new Date(eyear, emonth - 1, eday);
    if (endDate < startDate) return "End date cannot be before start date.";
    return null;
  }

  // Submit update to Firestore
  async function handleSubmit() {
    setStatus("");
    if (!foundDocId || foundOfferIndex === null) {
      setStatus("No offer loaded to update.");
      return;
    }

    const validationError = validateOffer(offer);
    if (validationError) {
      setStatus("❌ " + validationError);
      return;
    }

    setLoadingSearch(true);
    try {
      const docRef = doc(db, "product_offers", foundDocId);
      const docSnapSingle = await getDoc(docRef);
      const docData = docSnapSingle.data();
      if (!docData || !Array.isArray(docData.offers)) {
        setStatus("Offer document data missing or invalid.");
        setLoadingSearch(false);
        return;
      }

      const offers = [...docData.offers];
      // Prepare updated offer object
      const updatedOffer = {
        ...offers[foundOfferIndex],
        ...offer,
        originalPrice: offer.originalPrice ? parseFloat(offer.originalPrice) : null,
        offerPrice: parseFloat(offer.offerPrice),
        rebateAmount: offer.rebateAmount ? parseFloat(offer.rebateAmount) : 0,
        cashback: offer.cashback ? parseFloat(offer.cashback) : 0,
        freeShippingMinimum: offer.freeShippingMinimum
          ? parseFloat(offer.freeShippingMinimum)
          : 0,
        tags: offer.tags
          ? offer.tags.split(",").map((t) => t.trim()).filter((t) => t !== "")
          : [],
        startDttm: dateInputToTimestamp(offer.startDttm),
        endDttm: dateInputToTimestamp(offer.endDttm, true),
        uploadedDttm: Timestamp.now(),
        uploadedBy: uploadedBy || "admin@example.com",
      };

      offers[foundOfferIndex] = updatedOffer;

      await updateDoc(docRef, {
        offers,
        offerCount: offers.length,
      });

      // ✅ Reset form fields after update
      setOffer({
        productName: "",
        vendor: "",
        offerType: "",
        offerPrice: "",
        originalPrice: "",
        cashback: "",
        rebateAmount: "",
        shippingCost: "",
        freeShippingMinimum: "",
        link: "",
        image: "",
        promo: "",
        coupon: "",
        startDttm: "",
        endDttm: "",
        uploadedBy: uploadedBy || "admin@example.com",
        active: true,
        tags: "",
      });

      setFoundDocId(null);
      setFoundOfferIndex(null); 
      setStatus("✅ Offer updated successfully!");
    } catch (error) {
      console.error(error);
      setStatus("❌ Failed to update offer.");
    }
    setLoadingSearch(false);
  }

return (
  <div className="bg-white p-4 rounded-lg shadow-md max-w-3xl mx-auto">
    <div className="flex items-center justify-between mb-4">
      <h2 className="text-xl font-semibold">✏️ Update Product Offer</h2>
    </div>

    {/* Search inputs */}
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
      <div>
        <label className="block font-medium mb-1">Vendor</label>
        <Select
          options={vendorOptions}
          value={selectedVendor}
          onChange={setSelectedVendor}
          isClearable
          placeholder="Select Vendor"
          isDisabled={loadingSearch}
        />
      </div>
      <div>
        <label className="block font-medium mb-1">Product Name</label>
        <input
          type="text"
          className="border p-2 rounded w-full"
          placeholder="Enter Product Name"
          value={searchProductName}
          onChange={(e) => setSearchProductName(e.target.value)}
          disabled={loadingSearch}
        />
      </div>
      <div className="flex items-end">
        <button
          onClick={handleSearch}
          disabled={loadingSearch}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded shadow w-full"
        >
          {loadingSearch ? "Searching..." : "Search Offer"}
        </button>
      </div>
    </div>

    {foundDocId !== null && (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
        className="grid grid-cols-1 sm:grid-cols-2 gap-4"
      >
        {/* 1. Vendor */}
        <div>
          <label className="block font-medium mb-1">Vendor</label>
          <Select
            options={vendorOptions}
            value={selectedVendor}
            onChange={(option) => {
              setSelectedVendor(option);
              setOffer((prev) => ({ ...prev, vendor: option ? option.value : "" }));
            }}
            isClearable
            placeholder="Select Vendor"
            className="react-select-container"
          />
        </div>

        {/* 2. Product Name */}
        <div>
          <label className="block font-medium mb-1">Product Name</label>
          <input
            name="productName"
            value={offer.productName}
            onChange={handleChange}
            className="border p-2 rounded w-full"
            required
          />
        </div>

        {/* 3. Description */}
        <div>
          <label className="block font-medium mb-1">Description</label>
          <input
            name="description"
            value={offer.description}
            onChange={handleChange}
            className="border p-2 rounded w-full"
          />
        </div>

        {/* 4. Category */}
        <div>
          <label className="block font-medium mb-1">Category</label>
          <select
            name="category"
            value={offer.category}
            onChange={handleChange}
            className="border p-2 rounded w-full"
          >
            <option value="">Select Category</option>
            {categories.map((cat, idx) => (
              <option key={idx} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        </div>

        {/* 5. Offer Link */}
        <div>
          <label className="block font-medium mb-1">Offer Link</label>
          <input
            name="offerLink"
            value={offer.offerLink}
            onChange={handleChange}
            className="border p-2 rounded w-full"
          />
        </div>

        {/* 6. Offer Image URL */}
        <div>
          <label className="block font-medium mb-1">Offer Image URL</label>
          <input
            name="offerImage"
            value={offer.offerImage}
            onChange={handleChange}
            className="border p-2 rounded w-full"
          />
        </div>

        {/* Offer Image Preview */}
        {offer.offerImage && (
          <img
            src={offer.offerImage}
            alt="Offer Preview"
            className="max-h-20 rounded shadow col-span-full object-contain"
          />
        )}

        {/* 7. Original Price */}
        <div>
          <label className="block font-medium mb-1">Original Price</label>
          <input
            name="originalPrice"
            value={offer.originalPrice}
            onChange={handleChange}
            type="number"
            step="0.01"
            className="border p-2 rounded w-full"
          />
        </div>

        {/* 8. Offer Price */}
        <div>
          <label className="block font-medium mb-1">Offer Price</label>
          <input
            name="offerPrice"
            value={offer.offerPrice}
            onChange={handleChange}
            type="number"
            step="0.01"
            className="border p-2 rounded w-full"
            required
          />
        </div>

        {/* 11. Offer Type */}
        <div>
          <label className="block font-medium mb-1">Offer Type</label>
          <input
            name="offerType"
            value={offer.offerType}
            onChange={handleChange}
            className="border p-2 rounded w-full"
          />
        </div>

        {/* 9 & 10. Start Date & End Date side-by-side */}
        <div className="col-span-full flex gap-4">
          <div className="flex flex-col flex-1">
            <label className="block font-medium mb-1">Start Date</label>
            <input
              name="startDttm"
              value={offer.startDttm}
              onChange={handleChange}
              type="date"
              className="border p-2 rounded w-full"
              required
            />
          </div>
          <div className="flex flex-col flex-1">
            <label className="block font-medium mb-1">End Date</label>
            <input
              name="endDttm"
              value={offer.endDttm}
              onChange={handleChange}
              type="date"
              className="border p-2 rounded w-full"
              required
            />
          </div>
        </div>

        {/* 12. Free Store Pickup full row */}
        <div className="col-span-full">
          <label className="block font-medium mb-1">Free Store Pickup</label>
          <select
            name="freeStorePickup"
            value={offer.freeStorePickup}
            onChange={handleChange}
            className="border p-2 rounded w-full"
          >
            <option value="No">No</option>
            <option value="Yes">Yes</option>
          </select>
        </div>

        {/* 13. Free Shipping Minimum */}
        <div>
          <label className="block font-medium mb-1">Free Shipping Minimum</label>
          <input
            name="freeShippingMinimum"
            value={offer.freeShippingMinimum}
            onChange={handleChange}
            type="number"
            step="0.01"
            className="border p-2 rounded w-full"
          />
        </div>

        {/* 14. Promo Code */}
        <div>
          <label className="block font-medium mb-1">Promo Code</label>
          <input
            name="promoCode"
            value={offer.promoCode}
            onChange={handleChange}
            className="border p-2 rounded w-full"
          />
        </div>

        {/* 15. Rebate Amount */}
        <div>
          <label className="block font-medium mb-1">Rebate Amount</label>
          <input
            name="rebateAmount"
            value={offer.rebateAmount}
            onChange={handleChange}
            type="number"
            step="0.01"
            className="border p-2 rounded w-full"
          />
        </div>

        {/* 16. Cashback */}
        <div>
          <label className="block font-medium mb-1">Cashback</label>
          <input
            name="cashback"
            value={offer.cashback}
            onChange={handleChange}
            type="number"
            step="0.01"
            className="border p-2 rounded w-full"
          />
        </div>

        {/* 17. Product SKU */}
        <div>
          <label className="block font-medium mb-1">Product SKU</label>
          <input
            name="productSKU"
            value={offer.productSKU}
            onChange={handleChange}
            className="border p-2 rounded w-full"
          />
        </div>

        {/* 18. Product UPC */}
        <div>
          <label className="block font-medium mb-1">Product UPC</label>
          <input
            name="productUPC"
            value={offer.productUPC}
            onChange={handleChange}
            className="border p-2 rounded w-full"
          />
        </div>

        {/* 19. Instructions */}
        <div className="col-span-full">
          <label className="block font-medium mb-1">Instructions</label>
          <textarea
            name="instructions"
            value={offer.instructions}
            onChange={handleChange}
            rows={4}
            className="border p-2 rounded w-full"
          />
        </div>

        {/* 20. Tags */}
        <div className="col-span-full">
          <label className="block font-medium mb-1">Tags (comma-separated)</label>
          <textarea
            name="tags"
            value={offer.tags}
            onChange={handleChange}
            rows={2}
            className="border p-2 rounded w-full"
          />
        </div>

        {/* Submit Button */}
        <div className="col-span-full">
          <button
            type="submit"
            disabled={loadingSearch}
            className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded w-full"
          >
            {loadingSearch ? "Updating..." : "Update Offer"}
          </button>
        </div>
      </form>
    )}

    {status && (
      <p className="mt-4 font-semibold text-red-600 whitespace-pre-line">{status}</p>
    )}
  </div>
);




}
